import * as React from "react";
import styled, { css } from "styled-components";
import throttle from "lodash/throttle";

import { colors, media } from "../../common/utils/style";
import S3Uploader from "../../lib/S3Uploader";
import ImageComponent, { Image as CommonImage } from "../../common/components/Image";
import FollowButtonContainer from "../../company_followings/components/FollowButtonContainer";
import HeaderNav, { HeaderNavModels } from "./HeaderNav";
import Translate from "../../common/components/Translate";
import Icon from "../../common/components/Icon";
import Hover from "../../common/components/Hover";
import CoverImage from "./CoverImage";
import { generateClientId } from "../../lib/generateClientId";

type Image = WtdModels.Union<WtdModels.Partial<WtdModels.Image, "id">, CommonImage>;
type Company = WtdModels.Union<
  WtdModels.Partial<WtdModels.Company, "id" | "company_path" | "name" | "url" | "short_location">,
  HeaderNavModels["Company"]
>;

export interface HeaderModels {
  Company: Company;
  Image: Image;
}

const HOME_PATH_REGEXP = /^\/(next_)?companies\/[^\/]+\/?$/;

interface Props {
  company: Company;
  avatar: Image | null;
  background: Image | null;
  pathname: string;
  canBeFixed?: boolean;
  editable?: boolean;
  homeLoaded: boolean;
  isMember?: boolean;
  onEdit?(): void;
  onClickLink?(event: React.MouseEvent<HTMLLinkElement>): void;
  onChangeAvatar?(image: Image): void;
  onChangeBackground?(image: Image): void;
}

type PopupType = "avatar" | "background";

interface State {
  fixed: boolean;
  openAvatarPopup: boolean;
  openBackgroundPopup: boolean;
}

class Header extends React.PureComponent<Props, State> {
  public static defaultProps = {
    canBeFixed: true,
  };

  private navRef?: HTMLElement = undefined;
  private baseRef: HTMLElement | null = null;
  private baseOffsetY: number = 0;
  private onScrollThrottled: () => void;
  private avatarUploader: S3Uploader;
  private backgroundUploader: S3Uploader;

  constructor(props: Props) {
    super(props);
    this.state = {
      fixed: false,
      openAvatarPopup: false,
      openBackgroundPopup: false,
    };
    this.onScrollThrottled = throttle(this.onScroll, 50);
    this.avatarUploader = new S3Uploader({ id: "company-header-avatar" });
    this.backgroundUploader = new S3Uploader({ id: "company-header-background" });
  }

  public componentDidMount() {
    if (!this.baseRef) {
      return;
    }
    this.baseOffsetY = this.baseRef.getBoundingClientRect().top + window.pageYOffset;

    window.addEventListener("scroll", this.onScrollThrottled);
    this.avatarUploader.listen({
      done: ([url]) => {
        this.props.onChangeAvatar!({ id: generateClientId(), url });
      },
    });
    this.backgroundUploader.listen({
      done: ([url]) => {
        this.props.onChangeBackground!({ id: generateClientId(), url });
      },
    });
  }

  public componentWillUnmount() {
    window.removeEventListener("scroll", this.onScrollThrottled);
    this.avatarUploader.dispose();
    this.backgroundUploader.dispose();
  }

  public componentDidUpdate(prevProps: Props) {
    if (this.props.pathname !== prevProps.pathname) {
      this.fixScrollPosition();
    }
  }

  public fixScrollPosition = () => {
    if (!this.navRef || typeof window === "undefined") {
      return;
    }
    // NOTE(miyashiro): Use global-nav for fixing scroll position on mobile
    const globalNav = document.getElementById("global-nav");
    const offset = this.navRef.getBoundingClientRect();
    if (globalNav) {
      const globalNavOffset = globalNav.getBoundingClientRect();
      window.scrollTo(0, offset.top - globalNavOffset.height);
    } else if (this.state.fixed) {
      // TODO(miyashiro): Remove constant value and Recalc scroll position with top margin
      window.scrollTo(0, this.baseOffsetY + 70 /* To hide margin */);
    }
  };

  public onScroll = () => {
    const fixed = window.pageYOffset >= this.baseOffsetY;
    if (fixed !== this.state.fixed) {
      this.setState({ fixed });
    }
  };

  public togglePopup = (type: PopupType) => {
    if (type === "avatar") {
      this.setState({ openAvatarPopup: !this.state.openAvatarPopup });
    } else if (type === "background") {
      this.setState({ openBackgroundPopup: !this.state.openBackgroundPopup });
    }
  };

  public setNavRef = (el: HTMLElement) => {
    this.navRef = el;
  };

  public render() {
    const {
      company,
      avatar,
      background,
      pathname,
      canBeFixed,
      editable,
      onEdit,
      onClickLink,
      homeLoaded,
      isMember,
    } = this.props;
    const isHome = HOME_PATH_REGEXP.test(pathname); // TODO(stomk): Judge by routing
    const fixed = this.state.fixed && Boolean(canBeFixed);
    // Override fallback color
    const color = company.font_color_code === colors.gray2 ? colors.gray3 : company.font_color_code;
    const styleOptions = { fixed, color, isHome: isHome && homeLoaded };
    return (
      <Base innerRef={el => (this.baseRef = el)}>
        <CoverImageWrapper {...styleOptions}>
          <CoverImage image={background} editable={editable} uploader={this.backgroundUploader} />
        </CoverImageWrapper>
        <HeaderBar {...styleOptions}>
          <HeaderBarInner {...styleOptions}>
            <HeaderLeft {...styleOptions}>
              <LinkToTop {...styleOptions} href="/">
                <img
                  src="https://dubpy8abnqmkw.cloudfront.net/images/assets/wantedly-logo.svg"
                  width="44px"
                  height="44px"
                  alt="Wantedly"
                />
              </LinkToTop>
              <Separator {...styleOptions} />
              <Hover>
                {({ isHovered, ref }) => (
                  <AvatarWrapper {...styleOptions} innerRef={ref}>
                    <AvatarInner {...styleOptions}>
                      <AvatarImageLink href={company.company_path}>
                        {avatar && <AvatarImage image={avatar} size="100x100m" alt={company.name} lazy={false} />}
                      </AvatarImageLink>
                      {editable && (
                        <>
                          <Overlay
                            isHovered={isHovered}
                            onClick={e => {
                              e.preventDefault();
                              this.avatarUploader.choose();
                            }}
                          >
                            <AvatarHoverContent>
                              <AvatarHoverIcon name="wt-icon-camera-o-2" />
                            </AvatarHoverContent>
                          </Overlay>
                          <input {...this.avatarUploader.getPropsForInput()} style={{ display: "none" }} />
                          {/* TODO(kdnk): Comment in when you implement popup */}
                          {/* {this.state.openAvatarPopup && <ImageUploadPopup uploader={this.avatarUploader} />} */}
                        </>
                      )}
                    </AvatarInner>
                  </AvatarWrapper>
                )}
              </Hover>
              <Meta {...styleOptions}>
                <MetaUpper {...styleOptions}>
                  <CompanyName {...styleOptions}>
                    <CompanyNameLink href={company.company_path}>{company.name}</CompanyNameLink>
                  </CompanyName>
                  <FollowButtonUpper companyId={company.id} {...styleOptions} />
                </MetaUpper>
                <MetaLower {...styleOptions}>
                  <ButtonWrapper>
                    {editable ? (
                      <EditButton {...styleOptions} onClick={onEdit}>
                        <Translate id="company.home.edit_company_info" />
                      </EditButton>
                    ) : (
                      <FollowButtonLower companyId={company.id} {...styleOptions} />
                    )}
                  </ButtonWrapper>
                  {company.url && (
                    <OfficialLink {...styleOptions} href={company.url} target="_blank">
                      {company.url}
                    </OfficialLink>
                  )}
                  {company.short_location && (
                    <Location>
                      <LocationIcon className="wt-icon wt-icon-location" />
                      {company.short_location}
                    </Location>
                  )}
                </MetaLower>
              </Meta>
            </HeaderLeft>
            <NavWrapper {...styleOptions}>
              <NavWrapperInner>
                <HeaderNav innerRef={this.setNavRef} company={company} onClickLink={onClickLink} isMember={isMember} />
              </NavWrapperInner>
            </NavWrapper>
          </HeaderBarInner>
        </HeaderBar>
      </Base>
    );
  }
}

const MAIN_INNER_WIDTH = 980;
const EMBEDED_HEADER_HEIGHT = 100;
export const FIXED_HEADER_HEIGHT = 60;

interface StyleProps {
  fixed: boolean;
  color: string;
  isHome: boolean;
}

const Base = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  border-bottom: 1px solid ${colors.lightgray1};
  margin-bottom: 40px;
  @media ${media.large} {
    width: 100%;
    min-height: ${EMBEDED_HEADER_HEIGHT}px;
    margin-bottom: 80px;
    border: none;
  }
`;

const CoverImageWrapper = styled.div`
  @media ${media.large} {
    order: 1;
    display: ${(props: StyleProps) => (props.isHome ? "block" : "none")};
    margin-top: ${EMBEDED_HEADER_HEIGHT}px;
    position: relative;
  }
`;

interface OverlayStyleProps {
  isHovered: boolean;
}

const Overlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.4);
  visibility: ${(props: OverlayStyleProps) => (props.isHovered ? "visible" : "hidden")};
  opacity: ${(props: OverlayStyleProps) => (props.isHovered ? 1 : 0)};
  transition: all 200ms ease-out;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const AvatarWrapper = styled.div`
  flex-shrink: 0;
  position: absolute;
  top: -30px;
  left: 0;
  width: 75px;
  height: 75px;
  border-radius: 50%;
  border: solid 2px #ffffff;
  box-sizing: border-box;
  overflow: hidden;
  &:active,
  &:focus {
    position: absolute;
  }
  @media ${media.large} {
    position: relative;
    top: 0;
    border-radius: unset;
    border: none;
    overflow: visible;
    width: ${(props: StyleProps) => (props.fixed ? "44px" : "100px")};
    height: ${(props: StyleProps) => (props.fixed ? "44px" : "auto")};
    margin-right: ${(props: StyleProps) => (props.fixed ? "10px" : "20px")};
    &:active,
    &:focus {
      position: relative;
    }
  }
`;

const AvatarInner = styled.div`
  @media ${media.large} {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: ${(props: StyleProps) => (props.fixed ? "44px" : "100px")};
    border-radius: 50%;
    border: ${(props: StyleProps) => (props.fixed ? "none" : "solid 4px #ffffff")};
    box-sizing: border-box;
    overflow: hidden;
  }
`;

const AvatarImageLink = styled.a`
  display: block;
`;

const AvatarImage = styled(ImageComponent)`
  width: 100%;
`;

const AvatarHoverContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: #fff;
`;

const AvatarHoverIcon = styled(Icon)`
  font-size: 30px;
`;

const HeaderBar = styled.div`
  background-color: white;
  @media ${media.large} {
    position: ${(props: StyleProps) => (props.fixed ? "fixed" : "absolute")};
    box-shadow: ${(props: StyleProps) => (props.fixed ? "0 4px 8px 0 rgba(0, 0, 0, 0.06);" : "none")};
    transform: none;
    top: 0;
    left: 0;
    z-index: 20;
    width: 100%;
    min-height: ${(props: StyleProps) => (props.fixed ? "auto" : `${EMBEDED_HEADER_HEIGHT}px`)};
    height: ${(props: StyleProps) => (props.fixed ? `${FIXED_HEADER_HEIGHT}px` : "auto")};
  }
`;

const HeaderBarInner = styled.div`
  padding: 0 17px;
  display: flex;
  flex-direction: column;
  @media ${media.large} {
    width: ${(props: StyleProps) => (props.fixed ? "auto" : `${MAIN_INNER_WIDTH}px`)};
    height: ${(props: StyleProps) => (props.fixed ? "100%" : "auto")};
    margin: 0 auto;
    padding: 0;
    padding-top: ${(props: StyleProps) => (props.fixed ? "0" : "25px")};
    box-sizing: border-box;
    flex-direction: row;
    justify-content: space-between;
    align-items: ${(props: StyleProps) => (props.fixed ? "center" : "stretch")};
  }
`;

const HeaderLeft = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-bottom: 14px;
  @media ${media.large} {
    width: auto;
    height: ${(props: StyleProps) => (props.fixed ? "100%" : "auto")};
    margin-right: 50px;
    margin-bottom: 0;
    flex-direction: row;
    align-items: ${(props: StyleProps) => (props.fixed ? "center" : "stretch")};
  }
`;
const LinkToTop = styled.a`
  display: none;
  @media ${media.large} {
    display: ${(props: StyleProps) => (props.fixed ? "block" : "none")};
    height: 100%;
    padding: 8px;
    box-sizing: border-box;
    &:hover {
      background-color: ${colors.lightergray1};
    }
  }
`;

const Separator = styled.div`
  display: none;
  @media ${media.large} {
    display: ${(props: StyleProps) => (props.fixed ? "block" : "none")};
    width: 1px;
    height: 34px;
    margin: 0 16px 0 10px;
    background-color: rgba(0, 0, 0, 0.12);
  }
`;

const Meta = styled.div`
  @media ${media.large} {
    padding-bottom: ${(props: StyleProps) => (props.fixed ? "0" : "17px")};
  }
`;

const MetaUpper = styled.div`
  display: flex;
  flex-direction: column-reverse;
  margin-bottom: 6px;
  @media ${media.large} {
    flex-direction: row;
    align-items: center;
    margin-bottom: ${(props: StyleProps) => (props.fixed ? "0" : "6px")};
    height: ${(props: StyleProps) => (props.fixed ? "100%" : "auto")};
  }
`;

const ButtonWrapper = styled.div`
  @media ${media.large} {
    display: flex;
    margin-right: 10px;
  }
`;

const ButtonBase = css`
  min-width: 120px;
  height: 32px;
  @media ${media.large} {
    min-width: 90px;
    height: 26px;
  }
`;

const FollowButtonBase = styled(FollowButtonContainer)`
  ${ButtonBase};
  color: ${(props: StyleProps) => props.color};
  border-color: ${(props: StyleProps) => props.color};
`;

const FollowButtonLower = FollowButtonBase.extend`
  display: none;
  @media ${media.large} {
    display: flex;
  }
`;

const FollowButtonUpper = FollowButtonBase.extend`
  display: flex;
  margin-top: 13px;
  margin-left: auto;
  @media ${media.large} {
    display: ${(props: StyleProps) => (props.fixed ? "flex" : "none")};
    margin-top: 0;
    margin-left: 0;
  }
`;

const EditButton = styled.button`
  ${ButtonBase};
  padding: 0 20px;
  background-color: white;
  color: ${colors.gray4};
  border-color: ${colors.gray4};
  border-radius: 30px;
  font-family: inherit;
  font-weight: 400;
  font-size: 13px;
  line-height: 1;
`;

const CompanyName = styled.h1`
  font-weight: 600;
  font-size: 24px;
  margin-top: 11px;
  @media ${media.large} {
    font-weight: ${(props: StyleProps) => (props.fixed ? "500" : "600")};
    font-size: ${(props: StyleProps) => (props.fixed ? "13px" : "22px")};
    line-height: 26px;
    margin-top: 0;
    margin-right: 12px;
  }
`;

const CompanyNameLink = styled.a`
  color: ${colors.gray4} !important;
  transition: opacity 100ms;
  &:hover {
    text-decoration: none;
    opacity: 0.5;
  }
`;

const MetaLower = styled.div`
  display: flex;
  color: ${colors.gray2};
  @media ${media.large} {
    display: ${(props: StyleProps) => (props.fixed ? "none" : "flex")};
    align-items: center;
  }
`;

const OfficialLink = styled.a`
  font-size: 13px;
  margin-right: 14px;
  color: ${(props: StyleProps) => props.color} !important;
  @media ${media.large} {
    margin-right: 10px;
    color: ${colors.gray2} !important;
  }
`;

const Location = styled.span`
  font-size: 13px;
  display: inline-flex;
`;

const LocationIcon = styled.i`
  margin-top: -2px;
  margin-right: 3px;
`;

const NavWrapper = styled.div`
  width: 100vw;
  margin-left: -17px;
  display: flex;
  box-sizing: border-box;
  overflow: auto;
  @media ${media.large} {
    flex-shrink: 0;
    width: auto;
    margin-left: 0;
    padding-top: ${(props: StyleProps) => (props.fixed ? "0" : "10px")};
    padding-right: ${(props: StyleProps) => (props.fixed ? "30px" : "0")};
  }
`;

const NavWrapperInner = styled.div`
  padding: 0 17px;
  flex-shrink: 0;
  @media ${media.large} {
    padding: 0;
  }
`;

export default Header;



// WEBPACK FOOTER //
// ./frontend/src/companies/components/Header.tsx